import { useState } from "react";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";

const FUELS = ["Petrol", "Diesel", "Hybrid", "Electric"];
const TRANS = ["Automatic", "Manual"];

const initial = {
  name: "", email: "", phone: "", brand: "", model: "",
  year: "", mileage: "", fuel: "Petrol", transmission: "Automatic",
  expected_price: "", description: "", city: "",
};

export default function SellYourCar() {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.brand || !form.model || !form.year || !form.mileage) {
      toast.error("Please fill all required fields.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/sell-requests", {
        ...form,
        year: Number(form.year),
        mileage: Number(form.mileage),
        expected_price: form.expected_price ? Number(form.expected_price) : null,
      });
      toast.success("Valuation request received. We'll be in touch shortly.");
      setForm(initial);
      setDone(true);
    } catch {
      toast.error("Could not submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="sell-page" className="bg-[#FCFCFC] min-h-screen">
      <div className="border-b border-gray-100 bg-white">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16 py-16 md:py-20">
          <p className="overline mb-4">Sell Your Car</p>
          <h1 className="font-display text-5xl md:text-6xl tracking-tighter font-light max-w-3xl">
            A private valuation. <em className="not-italic gold-accent">In days, not weeks.</em>
          </h1>
          <p className="text-gray-600 mt-6 max-w-2xl">
            Share a few details and our specialists will revert with a market-led valuation, an off-market offer
            and a transparent path to settlement.
          </p>
        </div>
      </div>

      <div className="mx-auto max-w-[900px] px-6 md:px-12 py-16">
        {done ? (
          <div className="bg-white border border-gray-200 p-10 text-center" data-testid="sell-success">
            <p className="font-display text-3xl tracking-tight mb-2">Request received.</p>
            <p className="text-gray-600 mb-8">A senior consultant will contact you within 24 working hours.</p>
            <Button onClick={() => setDone(false)} className="btn-primary rounded-sm">Submit another</Button>
          </div>
        ) : (
        <form onSubmit={submit} className="bg-white border border-gray-200 p-8 md:p-12 space-y-8" data-testid="sell-form">
          <div>
            <p className="overline mb-5">Your details</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <Field label="Full name *" id="name">
                <Input id="name" data-testid="sell-name" value={form.name} onChange={(e)=>set("name", e.target.value)} />
              </Field>
              <Field label="Phone *" id="phone">
                <Input id="phone" data-testid="sell-phone" value={form.phone} onChange={(e)=>set("phone", e.target.value)} />
              </Field>
              <Field label="Email *" id="email">
                <Input id="email" type="email" data-testid="sell-email" value={form.email} onChange={(e)=>set("email", e.target.value)} />
              </Field>
              <Field label="City" id="city">
                <Input id="city" data-testid="sell-city" value={form.city} onChange={(e)=>set("city", e.target.value)} />
              </Field>
            </div>
          </div>

          <div className="pt-6 border-t border-gray-100">
            <p className="overline mb-5">Vehicle details</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <Field label="Brand *" id="brand"><Input id="brand" data-testid="sell-brand" value={form.brand} onChange={(e)=>set("brand", e.target.value)} placeholder="e.g. Mercedes-Benz" /></Field>
              <Field label="Model *" id="model"><Input id="model" data-testid="sell-model" value={form.model} onChange={(e)=>set("model", e.target.value)} placeholder="e.g. S-Class" /></Field>
              <Field label="Year *" id="year"><Input id="year" type="number" data-testid="sell-year" value={form.year} onChange={(e)=>set("year", e.target.value)} /></Field>
              <Field label="Mileage (km) *" id="mileage"><Input id="mileage" type="number" data-testid="sell-mileage" value={form.mileage} onChange={(e)=>set("mileage", e.target.value)} /></Field>
              <Field label="Fuel" id="fuel">
                <Select value={form.fuel} onValueChange={(v)=>set("fuel", v)}>
                  <SelectTrigger data-testid="sell-fuel"><SelectValue /></SelectTrigger>
                  <SelectContent>{FUELS.map(f => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Transmission" id="transmission">
                <Select value={form.transmission} onValueChange={(v)=>set("transmission", v)}>
                  <SelectTrigger data-testid="sell-transmission"><SelectValue /></SelectTrigger>
                  <SelectContent>{TRANS.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Expected price (₹)" id="price">
                <Input id="price" type="number" data-testid="sell-price" value={form.expected_price} onChange={(e)=>set("expected_price", e.target.value)} placeholder="Optional" />
              </Field>
            </div>
            <div className="mt-5">
              <Field label="Anything else we should know?" id="desc">
                <Textarea id="desc" rows={4} data-testid="sell-desc" value={form.description} onChange={(e)=>set("description", e.target.value)} placeholder="Single owner, service history, modifications, etc." />
              </Field>
            </div>
          </div>

          <div className="pt-4 flex justify-end">
            <Button type="submit" disabled={submitting} data-testid="sell-submit" className="btn-primary rounded-sm h-12 px-10">
              {submitting ? "Sending…" : "Request Valuation"}
            </Button>
          </div>
        </form>)}
      </div>
    </div>
  );
}

function Field({ label, id, children }) {
  return (
    <div>
      <Label htmlFor={id} className="text-xs uppercase tracking-[0.15em] text-gray-600">{label}</Label>
      <div className="mt-2">{children}</div>
    </div>
  );
}
